import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppActionMenu from '../../../../components/AppActionMenu';
import AppQuickAction from '../../../../components/AppQuickAction';

export default function LeaveApplicationTable({
  records = [],
  isLoading = false,
  onApprove,
  onReject,
  onDelete,
  onResetFilters,
}) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Leave Applications...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">assignment</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No Leave Applications Found</h3>
          <p className="text-xs text-on-surface-variant max-w-md">
            There are no leave applications matching your selected filters.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Employee</th>
              <th className="px-5 py-3.5">Leave Date</th>
              <th className="px-5 py-3.5">Days</th>
              <th className="px-5 py-3.5">Leave Type</th>
              <th className="px-5 py-3.5">Reason</th>
              <th className="px-5 py-3.5">Remarks</th>
              <th className="px-5 py-3.5 text-center">Status</th>
              <th className="px-5 py-3.5 text-center">Quick Action</th>
              <th className="w-20 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => {
              const isPending = item.status === 'Pending';
              const isApproved = item.status === 'Approved';
              const isRejected = item.status === 'Rejected';

              return (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/payroll/leave-application/entry/${item.id}`)}
                  className="transition-colors hover:bg-surface-container-low/50 cursor-pointer group"
                >
                  {/* Employee Name & Code */}
                  <td className="px-5 py-3.5 font-bold text-on-surface">
                    <div className="flex flex-col">
                      <span className="font-bold text-on-surface group-hover:text-primary transition-colors">
                        {item.employee}
                      </span>
                      <span className="text-[11px] font-normal text-on-surface-variant">
                        {item.employeeId}
                      </span>
                    </div>
                  </td>

                  {/* Leave Date */}
                  <td className="px-5 py-3.5 text-xs font-semibold text-on-surface whitespace-nowrap">
                    {item.leaveDateDisplay || item.fromDate}
                  </td>

                  {/* Days */}
                  <td className="px-5 py-3.5 text-xs font-bold text-on-surface">
                    <div className="flex items-center gap-1.5">
                      <span>{Number(item.days).toFixed(2)}</span>
                      {item.daysType !== 0 && (
                        <span className="rounded bg-surface-container-high px-1.5 py-0.5 text-[10px] font-semibold text-on-surface-variant">
                          {item.type}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Leave Type with color pill & alias */}
                  <td className="px-5 py-3.5 text-xs font-semibold text-on-surface">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10"
                        style={{ backgroundColor: item.leaveTypeColor || '#3B82F6' }}
                      />
                      <span>{item.leaveType}</span>
                      {item.leaveTypeAlias && (
                        <span className="rounded bg-surface-container-high px-1 py-0.5 text-[10px] font-bold text-on-surface-variant">
                          {item.leaveTypeAlias}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Reason */}
                  <td className="px-5 py-3.5 text-xs text-on-surface">
                    <span className="inline-flex items-center rounded-md bg-surface-container-high px-2 py-0.5 text-xs font-medium text-on-surface">
                      {item.reason}
                    </span>
                  </td>

                  {/* Remarks */}
                  <td className="px-5 py-3.5 text-xs text-on-surface-variant max-w-xs truncate">
                    {item.remarks || '-'}
                  </td>

                  {/* Status Badge */}
                  <td className="px-5 py-3.5 text-center">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                        isApproved
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                          : isRejected
                          ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
                          : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          isApproved
                            ? 'bg-emerald-500'
                            : isRejected
                            ? 'bg-rose-500'
                            : 'bg-amber-500'
                        }`}
                      />
                      {item.status}
                    </span>
                  </td>

                  {/* Quick Action Column */}
                  <td
                    className="px-5 py-3.5 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isPending ? (
                      <AppQuickAction
                        status={item.status}
                        onApprove={() => onApprove?.(item.id, item.employee)}
                        onReject={() => onReject?.(item.id, item.employee)}
                        approveTitle="Approve Leave"
                        rejectTitle="Reject Leave"
                      />
                    ) : (
                      <span className="text-xs text-on-surface-variant/60 font-semibold">—</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td
                    className="px-5 py-3.5 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AppActionMenu
                      triggerLabel=""
                      triggerIcon="more_vert"
                      showChevron={false}
                      triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                      items={[
                        { key: 'edit', label: 'Edit Application', icon: 'edit' },
                        ...(isPending
                          ? [
                              { key: 'approve', label: 'Approve', icon: 'check_circle' },
                              { key: 'reject', label: 'Reject', icon: 'cancel' },
                            ]
                          : []),
                        ...(!isApproved
                          ? [{ key: 'delete', label: 'Delete', icon: 'delete', danger: true }]
                          : []),
                      ]}
                      onSelect={(key) => {
                        if (key === 'edit') {
                          navigate(`/payroll/leave-application/entry/${item.id}`);
                        } else if (key === 'approve') {
                          onApprove?.(item.id, item.employee);
                        } else if (key === 'reject') {
                          onReject?.(item.id, item.employee);
                        } else if (key === 'delete') {
                          onDelete?.(item.id, item.employee);
                        }
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
